
import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { InstagramIntegration } from "../types";
import { instagramIntegrationService } from "../services/InstagramIntegrationService";
import { useIntegrationState } from "../../common/hooks/useIntegrationState";

/**
 * Hook that manages Instagram integration state and operations
 */
export const useInstagramIntegration = (aiStaffId: string = "1") => {
  // Use the shared integration state hook
  const {
    isConnected,
    setIsConnected,
    connectionError,
    setConnectionError,
    isLoading,
    setIsLoading,
    isChecking,
    setIsChecking,
    isInitialized,
    setIsInitialized
  } = useIntegrationState();
  
  // Instagram-specific state
  const [username, setUsername] = useState("");
  const [isContentSchedulingEnabled, setIsContentSchedulingEnabled] = useState(true);
  const [isAutoResponsesEnabled, setIsAutoResponsesEnabled] = useState(true);
  
  const queryClient = useQueryClient();
  
  // Fetch Instagram integration data
  useQuery({
    queryKey: ['instagramIntegration', aiStaffId],
    queryFn: async () => {
      try {
        setIsChecking(true);
        const data = await instagramIntegrationService.fetchIntegration(aiStaffId);
        
        if (data) {
          setIsConnected(true);
          setUsername(data.instagram_username || "");
          setIsContentSchedulingEnabled(!!data.is_content_scheduling_enabled);
          setIsAutoResponsesEnabled(!!data.is_auto_responses_enabled);
          setConnectionError(null);
        } else {
          setIsConnected(false);
        }
        
        return data;
      } catch (error) {
        logger.error({
          message: "Error loading Instagram integration",
          error: error instanceof Error ? error : new Error(String(error)),
          context: { aiStaffId }
        });
        setConnectionError("Failed to load integration data");
        return null;
      } finally {
        setIsChecking(false);
        setIsInitialized(true);
      }
    },
    enabled: !!aiStaffId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false
  });
  
  // Connect to Instagram
  const handleConnect = useCallback(async (instagramUsername: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Simulate connection process
      // In a real integration, this would perform API calls to Instagram
      const integration: InstagramIntegration = {
        ai_staff_id: aiStaffId,
        instagram_username: instagramUsername,
        is_content_scheduling_enabled: true,
        is_auto_responses_enabled: true,
        is_enabled: true
      };
      
      const success = await instagramIntegrationService.saveIntegration(integration);
      
      if (success) {
        setIsConnected(true);
        setUsername(instagramUsername);
        setConnectionError(null);
        
        // Invalidate cache to refresh data
        queryClient.invalidateQueries({
          queryKey: ['instagramIntegration', aiStaffId]
        });
        
        toast.success("Successfully connected to Instagram");
        return true;
      } else {
        setConnectionError("Failed to save integration data");
        toast.error("Failed to connect to Instagram");
        return false;
      }
    } catch (error) {
      logger.error({
        message: "Error connecting to Instagram",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId, username: instagramUsername }
      });
      setConnectionError("Connection error: " + (error instanceof Error ? error.message : String(error)));
      toast.error("Error connecting to Instagram");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [aiStaffId, queryClient, setConnectionError, setIsConnected, setIsLoading]);
  
  // Disconnect from Instagram
  const handleDisconnect = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const success = await instagramIntegrationService.deleteIntegration(aiStaffId);
      
      if (success) {
        setIsConnected(false);
        setUsername("");
        
        // Invalidate cache to refresh data
        queryClient.invalidateQueries({
          queryKey: ['instagramIntegration', aiStaffId]
        });
        
        toast.success("Successfully disconnected from Instagram");
        return true;
      } else {
        toast.error("Failed to disconnect from Instagram");
        return false;
      }
    } catch (error) {
      logger.error({
        message: "Error disconnecting from Instagram",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      toast.error("Error disconnecting from Instagram");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [aiStaffId, queryClient, setIsConnected, setIsLoading]);
  
  // Handle content scheduling change
  const handleContentSchedulingChange = useCallback(async (checked: boolean): Promise<void> => {
    setIsContentSchedulingEnabled(checked);
    
    try {
      await instagramIntegrationService.updateInstagramSettings(aiStaffId, {
        is_content_scheduling_enabled: checked
      });
    } catch (error) {
      logger.error({
        message: "Error updating content scheduling setting",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId, value: checked }
      });
      toast.error("Failed to update setting");
    }
  }, [aiStaffId]);
  
  // Handle auto responses change
  const handleAutoResponsesChange = useCallback(async (checked: boolean): Promise<void> => {
    setIsAutoResponsesEnabled(checked);
    
    try {
      await instagramIntegrationService.updateInstagramSettings(aiStaffId, {
        is_auto_responses_enabled: checked
      });
    } catch (error) {
      logger.error({
        message: "Error updating auto responses setting",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId, value: checked }
      });
      toast.error("Failed to update setting");
    }
  }, [aiStaffId]);
  
  return {
    isConnected,
    connectionError,
    isLoading,
    isChecking,
    username,
    setUsername,
    isContentSchedulingEnabled,
    isAutoResponsesEnabled,
    handleConnect,
    handleDisconnect,
    handleContentSchedulingChange,
    handleAutoResponsesChange
  };
};
